import { useMemo } from 'react'

import { useIntl } from 'react-intl'
import * as Yup from 'yup'

import type { BidPrecision } from '@b-stock/fe-core/bstock-next/hooks'
import { usePrecisionNumberFormat } from '@b-stock/fe-core/bstock-react'

export const useBidAmountSchema = ({
  bidPrecision,
  currency,
  isRelativeBid,
  userMinBidAmount,
}: {
  bidPrecision?: BidPrecision
  currency: string
  isRelativeBid?: boolean
  userMinBidAmount: number
}) => {
  const { formatMessage } = useIntl()

  const { formatPrecisionNumber } = usePrecisionNumberFormat({
    currency,
    format: isRelativeBid ? 'percent' : 'currency',
    precision: bidPrecision,
    precisionStored: 1,
  })

  const BidAmountSchema = useMemo(() => {
    const minBidError = formatMessage(
      { id: 'Bid.error.minBid' },
      { amount: formatPrecisionNumber(userMinBidAmount) }
    )
    return Yup.object().shape({
      amount: Yup.lazy((value) => {
        if (value === undefined || value === '') {
          return Yup.string().required(minBidError)
        }
        return Yup.number()
          .min(userMinBidAmount, minBidError)
          .transform((_, val) => {
            const valNum = Number(val)
            return val === valNum ? valNum : ''
          })
      }),
    })
  }, [formatMessage, formatPrecisionNumber, userMinBidAmount])

  return BidAmountSchema
}
